import { getWithAuth, postWithAuthLocal, putWithAuthLocal } from './api/client';
import apiVariables from './api/apiVariables';
import type {
  AvailabilityActionResponse,
  AvailabilityApprovalActionPayload,
  AvailabilityApprovalPagedResult,
  AvailabilityApprovalQuery,
  AvailabilityApprovalResponse,
  AvailabilityApprovalRow,
  AvailabilityApprovalRejectPayload,
  AvailabilityApprovalDetails,
  ManagerAvailabilityUpdatePayload,
} from '../types/admin';

const toAvailabilityTypes = (value: any): string[] => {
  // Older API deployments returned either a comma-separated name or differently
  // shaped lookup objects. Normalize those contracts at the service boundary so
  // the approvals UI can work with one stable representation.
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.availabilityTypeName || item?.name || item?.label || '';
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
};

const getEmployeeName = (item: any) => {
  const directName = item.userName || item.name;
  if (directName) return directName;

  const firstName = item.user?.firstName || '';
  const lastName = item.user?.lastName || '';
  return `${firstName} ${lastName}`.trim() || 'Employee';
};

const normalizeRow = (item: any): AvailabilityApprovalRow => ({
  availabilityIds: Array.isArray(item.availabilityIds) ? item.availabilityIds : [],
  userId: item.userId || item.user?.userId || '',
  userName: getEmployeeName(item),
  email: item.email || item.user?.email || item.user?.username || null,
  submittedDate: item.submittedDate || '',
  approvalStatus: item.approvalStatus || 'Pending',
  availableHoursPerWeek: Number(item.availableHoursPerWeek) || 0,
  availabilityTypes: toAvailabilityTypes(item.availabilityTypes || item.availabilityTypeName || item.availabilities),
  notes: item.notes || null,
  approvedDate: item.approvedDate || null,
  rejectedDate: item.rejectedDate || null,
  rejectionReason: item.rejectionReason || null,
  effectiveFrom: item.effectiveFrom || null,
  effectiveTo: item.effectiveTo || null,
  assignedApproverUserId: item.assignedApproverUserId || null,
  assignedBackupApproverUserId: item.assignedBackupApproverUserId || null,
  authorityMappingId: item.authorityMappingId ?? null,
  approvedBy: item.approvedBy || item.primaryApprover?.name || null,
  rejectedBy: item.rejectedBy || item.primaryApprover?.name || null,

});

const normalizePagedResponse = (
  response: AvailabilityApprovalResponse | AvailabilityApprovalRow[]
): AvailabilityApprovalPagedResult => {
  // Support both the legacy bare-array response and the two paged response
  // envelopes used by current endpoints during a rolling API/Web deployment.
  if (Array.isArray(response)) {
    const data = response.map(normalizeRow);
    return {
      data,
      recordsTotal: data.length,
      recordsFiltered: data.length,
    };
  }

  const responseData = response?.data;

  if (Array.isArray(responseData)) {
    const data = responseData.map(normalizeRow);
    return {
      data,
      recordsTotal: response.recordsTotal ?? response.totalCount ?? data.length,
      recordsFiltered: response.recordsFiltered ?? response.totalCount ?? data.length,
    };
  }

  if (responseData && Array.isArray((responseData as AvailabilityApprovalPagedResult).data)) {
    const paged = responseData as AvailabilityApprovalPagedResult;
    const data = paged.data.map(normalizeRow);
    return {
      data,
      recordsTotal: paged.recordsTotal ?? response.recordsTotal ?? data.length,
      recordsFiltered: paged.recordsFiltered ?? response.recordsFiltered ?? data.length,
    };
  }

  throw new Error('The availability approvals API returned an unexpected response.');
};

export const getPendingAvailability = async (
  query: AvailabilityApprovalQuery
): Promise<AvailabilityApprovalPagedResult> => {
  const response = await getWithAuth<AvailabilityApprovalResponse | AvailabilityApprovalRow[]>(
    apiVariables.UserAvailabilityPending,
    {
      pageNumber: query.pageNumber,
      pageSize: query.pageSize,
      // Send both paging conventions because older API versions use DataTables
      // offsets while newer versions accept pageNumber/pageSize.
      start: (query.pageNumber - 1) * query.pageSize,
      length: query.pageSize,
      search: query.search,
      orderColumn: query.sortColumn,
      orderDirection: query.sortDirection,
      approvalStatus: query.filters.approvalStatus || undefined,
      submittedFrom: query.filters.submittedFrom || undefined,
      submittedTo: query.filters.submittedTo || undefined,
      hoursPerWeek: query.filters.hoursPerWeek || undefined,
      availabilityType: query.filters.availabilityType || undefined,
    }
  );

  return normalizePagedResponse(response);
};

export const getPendingAvailabilityCount = async () => {
  const response = await getPendingAvailability({
    pageNumber: 1,
    pageSize: 1,
    search: '',
    sortColumn: 'SubmittedDate',
    sortDirection: 'Descending',
    filters: {
      approvalStatus: 'Pending',
      submittedFrom: '',
      submittedTo: '',
      hoursPerWeek: '',
      availabilityType: '',
    },
  });

  return response.recordsFiltered;
};

export const approveAvailability = (payload: AvailabilityApprovalActionPayload) =>
  postWithAuthLocal<AvailabilityActionResponse>(
    apiVariables.UserAvailabilityApprove,
    payload
  );

export const rejectAvailability = (payload: AvailabilityApprovalRejectPayload) =>
  postWithAuthLocal<AvailabilityActionResponse>(
    apiVariables.UserAvailabilityReject,
    payload
  );

const userAvailabilityBase = apiVariables.UserAvailabilityPending.replace(/\/pending$/, '');

export const getAvailabilityApprovalDetails = async (availabilityId: number) => {
  const response = await getWithAuth<{ data?: AvailabilityApprovalDetails }>(`${userAvailabilityBase}/${availabilityId}/approval-details`);
  if (!response?.data) throw new Error('The availability details API returned an unexpected response.');
  return response.data;
};

export const managerUpdateAvailability = async (availabilityId: number, payload: ManagerAvailabilityUpdatePayload) => {
  const response = await putWithAuthLocal<{ data?: AvailabilityApprovalDetails }>(`${userAvailabilityBase}/${availabilityId}/manager-update`, payload);
  if (!response?.data) throw new Error('The availability update API returned an unexpected response.');
  return response.data;
};
